import type { AxiosInstance } from 'axios'
import {
  buildNotificationRuleUpdateBody,
  parseInAppNotificationListResult,
  parseNotificationRuleList,
  parseNotificationTemplateList,
  parseNotificationTemplatePreview,
  parseUnreadNotificationCount,
  parseUserNotificationPreferenceList,
  type InAppNotificationListParams,
  type InAppNotificationListResult,
  type NotificationRule,
  type NotificationTemplate,
  type NotificationTemplatePreview,
  type UserNotificationPreference,
} from './notifications-types'

export async function fetchInAppNotifications(
  client: AxiosInstance,
  params: InAppNotificationListParams = {},
): Promise<InAppNotificationListResult> {
  const res = await client.get<unknown>('/notifications', {
    params: {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    },
  })
  return parseInAppNotificationListResult(res.data)
}

export async function fetchUnreadNotificationCount(client: AxiosInstance): Promise<number> {
  const res = await client.get<unknown>('/notifications/unread-count')
  return parseUnreadNotificationCount(res.data)
}

export async function markNotificationRead(
  client: AxiosInstance,
  notificationId: string,
): Promise<void> {
  await client.put(`/notifications/${notificationId}/read`)
}

export async function markAllNotificationsRead(client: AxiosInstance): Promise<void> {
  await client.put('/notifications/read-all')
}

export async function fetchNotificationRules(client: AxiosInstance): Promise<NotificationRule[]> {
  const res = await client.get<unknown>('/notification-rules')
  return parseNotificationRuleList(res.data)
}

export async function putNotificationRule(
  client: AxiosInstance,
  ruleId: string,
  rule: NotificationRule,
): Promise<void> {
  await client.put(`/notification-rules/${ruleId}`, buildNotificationRuleUpdateBody(rule))
}

export async function fetchNotificationTemplates(
  client: AxiosInstance,
): Promise<NotificationTemplate[]> {
  const res = await client.get<unknown>('/notification-rules/templates')
  return parseNotificationTemplateList(res.data)
}

export async function putNotificationTemplate(
  client: AxiosInstance,
  templateId: string,
  body: { bodyTemplate: string; subjectTemplate?: string | null },
): Promise<void> {
  await client.put(`/notification-rules/templates/${templateId}`, {
    bodyTemplate: body.bodyTemplate,
    subjectTemplate: body.subjectTemplate ?? null,
  })
}

export async function postResetNotificationTemplate(
  client: AxiosInstance,
  templateId: string,
): Promise<void> {
  await client.post(`/notification-rules/templates/${templateId}/reset`)
}

export async function postNotificationTemplatePreview(
  client: AxiosInstance,
  body: {
    subjectTemplate: string
    bodyTemplate: string
    placeholders: Record<string, string>
  },
): Promise<NotificationTemplatePreview> {
  const res = await client.post<unknown>('/notification-rules/templates/preview', body)
  return parseNotificationTemplatePreview(res.data)
}

export async function fetchNotificationPreferences(
  client: AxiosInstance,
): Promise<UserNotificationPreference[]> {
  const res = await client.get<unknown>('/notification-preferences')
  return parseUserNotificationPreferenceList(res.data)
}

export async function putNotificationPreference(
  client: AxiosInstance,
  eventType: number,
  mutedChannels: number,
): Promise<void> {
  await client.put(`/notification-preferences/${eventType}`, { mutedChannels })
}

export async function deleteNotificationPreference(
  client: AxiosInstance,
  eventType: number,
): Promise<void> {
  await client.delete(`/notification-preferences/${eventType}`)
}
